import { QueryCache, QueryClient } from "@tanstack/react-query";
import {
	createTRPCClient,
	createWSClient,
	httpBatchLink,
	splitLink,
	wsLink,
} from "@trpc/client";
import { createTRPCOptionsProxy } from "@trpc/tanstack-react-query";
import { toast } from "sonner";
import type { AppRouter } from "../../../server/src/app-router";

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 1000 * 60 * 5, // 5 minutes
			refetchOnWindowFocus: false,
			refetchOnMount: false,
			retry: 1,
		},
	},
	queryCache: new QueryCache({
		onError: (error) => {
			toast.error(error.message, {
				action: {
					label: "retry",
					onClick: () => {
						queryClient.invalidateQueries();
					},
				},
			});
		},
	}),
});

// Create WebSocket client for subscriptions with lazy connection
const wsClient = createWSClient({
	url: import.meta.env.VITE_WS_URL,
	lazy: {
		enabled: true,
		closeMs: 1000 * 60 * 5, // Close connection after 5 minutes of inactivity
	},
	onClose: () => {
		// Silently handle connection close (prevents console errors during suspension)
	},
});

export const trpcClient = createTRPCClient<AppRouter>({
	links: [
		// Split link to route subscriptions to WebSocket and queries/mutations to HTTP
		splitLink({
			condition: (op) => op.type === "subscription" || op.path.includes("WebSocket"),
			true: wsLink({
				client: wsClient,
			}),
			false: httpBatchLink({
				url: `${import.meta.env.VITE_SERVER_URL}/trpc`,
				fetch(url, options) {
					return fetch(url, {
						...options,
						credentials: "include",
					});
				},
			}),
		}),
	],
});

export const trpc = createTRPCOptionsProxy<AppRouter>({
	client: trpcClient,
	queryClient,
});

// Helper to close WebSocket connection on cleanup
export const closeWebSocketConnection = () => {
	wsClient.close();
};
